import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getListing, createConversation, createShowing, photoUrl, getFileUrl, createCheckout } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import PaymentMethodSelector from "../components/PaymentMethodSelector";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import {
  MapPin, Bed, Bath, CheckCircle, Shield, Calendar as CalIcon,
  MessageSquare, ArrowLeft, Clock, PawPrint, Maximize, Home, Video, Play
} from "lucide-react";

const typeLabels = { room: "Room", apartment: "Apartment", house: "House" };
const leaseLabels = { short: "Short-Term (30-90 days)", mid: "Mid-Term (3-12 months)", long: "Long-Term (1 year+)" };

const defaultImages = {
  room: "https://images.unsplash.com/photo-1662454419716-c4c504728811?w=1200",
  apartment: "https://images.unsplash.com/photo-1666282167632-c613fbeb163c?w=1200",
  house: "https://images.pexels.com/photos/279719/pexels-photo-279719.jpeg?w=1200",
};

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msgText, setMsgText] = useState("");
  const [showingDate, setShowingDate] = useState(null);
  const [showingTime, setShowingTime] = useState("10:00");
  const [sendingMsg, setSendingMsg] = useState(false);
  const [bookingShowing, setBookingShowing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await getListing(id);
        setListing(res.data);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    })();
  }, [id]);

  const handleMessage = async () => {
    if (!msgText.trim() || !user) return;
    setSendingMsg(true);
    try {
      await createConversation(listing.landlord_id, msgText, listing.listing_id);
      setMsgText("");
      navigate("/messages");
    } catch (err) {
      console.error(err);
    }
    setSendingMsg(false);
  };

  const handleBookShowing = async () => {
    if (!showingDate || !user) return;
    setBookingShowing(true);
    try {
      await createShowing({
        listing_id: listing.listing_id,
        date: showingDate.toISOString().split("T")[0],
        time: showingTime,
      });
      navigate("/dashboard");
    } catch (err) {
      console.error(err);
    }
    setBookingShowing(false);
  };

  const handleApply = async () => {
    if (!user) return;
    try {
      const res = await createCheckout("rent", window.location.origin, listing.listing_id);
      window.location.href = res.data.url;
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "#faf8f3" }}>
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-[400px] bg-[#e5e3de] rounded-3xl" />
            <div className="h-8 bg-[#e5e3de] rounded w-1/2" />
            <div className="h-6 bg-[#e5e3de] rounded w-1/3" />
          </div>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "#faf8f3" }}>
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 text-center">
          <h2 className="text-2xl font-bold text-[#0d0d0d] font-['Fraunces']">Listing not found</h2>
        </div>
      </div>
    );
  }

  const photo = photoUrl(listing.photos?.[0]) || defaultImages[listing.property_type] || defaultImages.apartment;
  const isExternal = photo.startsWith("http");

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#faf8f3" }}>
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-[#5a5a5a] hover:text-[#0d0d0d] mb-6 transition-colors" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="rounded-3xl overflow-hidden mb-6 relative">
              <img
                src={isExternal ? photo : undefined}
                alt={listing.title}
                className="w-full aspect-[16/10] object-cover"
                onError={(e) => { e.target.src = defaultImages[listing.property_type]; }}
              />
              <div className="absolute top-4 left-4 flex gap-2">
                {listing.verified && (
                  <Badge className="bg-[#1a6b3c] text-white border-0 rounded-full px-3 py-1.5 flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5" /> Verified
                  </Badge>
                )}
                {listing.bah_eligible && (
                  <Badge className="bg-[#0d0d0d] text-white border-0 rounded-full px-3 py-1.5 flex items-center gap-1">
                    <Shield className="w-3.5 h-3.5" /> BAH Eligible
                  </Badge>
                )}
              </div>
            </div>

            {/* Property Tour Video */}
            {listing.video_path && (
              <div className="rounded-2xl overflow-hidden mb-6 bg-[#0d0d0d]" data-testid="listing-tour-video">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
                  <Video className="w-4 h-4 text-[#c8f563]" />
                  <span className="text-sm font-semibold text-white">Property Tour</span>
                </div>
                <video
                  controls
                  className="w-full max-h-[360px] object-contain"
                  poster={photoUrl(listing.photos?.[0])}
                  data-testid="listing-video-player"
                >
                  <source src={getFileUrl(listing.video_path)} />
                  Your browser does not support video.
                </video>
              </div>
            )}

            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-[#0d0d0d] font-['Fraunces'] mb-2" data-testid="listing-title">
                  {listing.title}
                </h1>
                <div className="flex items-center gap-2 text-[#5a5a5a]">
                  <MapPin className="w-4 h-4" />
                  <span>{listing.location}, {listing.city}, {listing.state}</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-[#1a6b3c]" data-testid="listing-price">
                  ${listing.price?.toLocaleString()}
                </p>
                <p className="text-sm text-[#5a5a5a]">per month</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 py-4 border-y border-[#d5d3ce] mb-6">
              <div className="flex items-center gap-2 text-[#0d0d0d]">
                <Home className="w-5 h-5 text-[#1a6b3c]" />
                <span className="font-medium">{typeLabels[listing.property_type]}</span>
              </div>
              <div className="flex items-center gap-2 text-[#0d0d0d]">
                <Bed className="w-5 h-5 text-[#1a6b3c]" />
                <span>{listing.bedrooms} Bed</span>
              </div>
              <div className="flex items-center gap-2 text-[#0d0d0d]">
                <Bath className="w-5 h-5 text-[#1a6b3c]" />
                <span>{listing.bathrooms} Bath</span>
              </div>
              {listing.sqft && (
                <div className="flex items-center gap-2 text-[#0d0d0d]">
                  <Maximize className="w-5 h-5 text-[#1a6b3c]" />
                  <span>{listing.sqft} sqft</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-[#0d0d0d]">
                <Clock className="w-5 h-5 text-[#1a6b3c]" />
                <span>{leaseLabels[listing.lease_type]}</span>
              </div>
              {listing.pet_friendly && (
                <div className="flex items-center gap-2 text-[#1a6b3c]">
                  <PawPrint className="w-5 h-5" />
                  <span>Pet Friendly</span>
                </div>
              )}
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold text-[#0d0d0d] mb-3 font-['Fraunces']">About this place</h2>
              <p className="text-[#5a5a5a] leading-relaxed whitespace-pre-line">{listing.description}</p>
            </div>

            {listing.amenities?.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-[#0d0d0d] mb-3 font-['Fraunces']">Amenities</h2>
                <div className="flex flex-wrap gap-2">
                  {listing.amenities.map(a => (
                    <Badge key={a} variant="secondary" className="bg-[#faf8f3] text-[#0d0d0d] border border-[#d5d3ce] rounded-full px-3 py-1.5 text-sm">
                      {a}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-4">
              {/* Landlord Card */}
              {listing.landlord && (
                <div className="bg-white rounded-2xl p-6 border border-[#d5d3ce]/60 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    {listing.landlord.picture ? (
                      <img src={listing.landlord.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#1a6b3c] flex items-center justify-center text-white font-bold">
                        {listing.landlord.name?.charAt(0)}
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-[#0d0d0d]">{listing.landlord.name}</p>
                      {listing.landlord.trust_score > 0 && (
                        <p className="text-sm text-[#5a5a5a]">Trust Score: {listing.landlord.trust_score}/5</p>
                      )}
                    </div>
                  </div>

                  {user && user.user_id !== listing.landlord_id && (
                    <div className="space-y-3">
                      <Textarea
                        data-testid="message-landlord-input"
                        value={msgText}
                        onChange={(e) => setMsgText(e.target.value)}
                        placeholder="Hi! I'm interested in this listing..."
                        className="rounded-xl border-[#d5d3ce] min-h-[80px]"
                      />
                      <Button
                        data-testid="send-message-btn"
                        onClick={handleMessage}
                        disabled={sendingMsg || !msgText.trim()}
                        className="w-full rounded-xl bg-[#1a6b3c] hover:bg-[#15562f] text-white"
                      >
                        <MessageSquare className="w-4 h-4 mr-2" /> Send Message
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              {user && user.role === "renter" && (
                <div className="bg-white rounded-2xl p-6 border border-[#d5d3ce]/60 shadow-sm space-y-3">
                  {/* Schedule Showing */}
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button data-testid="schedule-showing-btn" variant="outline" className="w-full rounded-xl border-[#1a6b3c] text-[#1a6b3c] hover:bg-[#1a6b3c] hover:text-white">
                        <CalIcon className="w-4 h-4 mr-2" /> Schedule Showing
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white rounded-2xl">
                      <DialogHeader>
                        <DialogTitle className="font-['Fraunces']">Schedule a Showing</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <Calendar
                          mode="single"
                          selected={showingDate}
                          onSelect={setShowingDate}
                          className="rounded-xl border border-[#d5d3ce]"
                          disabled={(date) => date < new Date()}
                        />
                        <div>
                          <label className="text-sm font-medium mb-1 block">Time</label>
                          <Input
                            type="time"
                            value={showingTime}
                            onChange={(e) => setShowingTime(e.target.value)}
                            className="rounded-xl border-[#d5d3ce]"
                            data-testid="showing-time-input"
                          />
                        </div>
                        <Button
                          data-testid="confirm-showing-btn"
                          onClick={handleBookShowing}
                          disabled={!showingDate || bookingShowing}
                          className="w-full rounded-xl bg-[#c8f563] text-[#0d0d0d] hover:bg-[#b8e553] font-semibold"
                        >
                          {bookingShowing ? "Booking..." : "Confirm Showing"}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Apply / Pay Rent */}
                  <PaymentMethodSelector
                    paymentType="rent"
                    listingId={listing.listing_id}
                    amount={listing.price}
                    buttonLabel="Apply & Pay First Month"
                    buttonClass="w-full rounded-xl bg-[#c8f563] text-[#0d0d0d] hover:bg-[#b8e553] font-semibold"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
